import { useCallback } from "react";
import {
  useMutation,
  type MutateOptions,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useOrganization } from "@/hooks/useOrganization";

/** The variables a pinned mutation's functions receive: the caller's, plus the church the call was made in. */
export type OrgPinned<TVars> = TVars & { organizationId: string | null };

export type OrgPinnedMutation<TData, TVars, TContext> = Omit<
  UseMutationResult<TData, Error, OrgPinned<TVars>, TContext>,
  "mutate" | "mutateAsync"
> & {
  mutate: (variables: TVars, options?: MutateOptions<TData, Error, OrgPinned<TVars>, TContext>) => void;
  mutateAsync: (variables: TVars, options?: MutateOptions<TData, Error, OrgPinned<TVars>, TContext>) => Promise<TData>;
};

/**
 * `useMutation` whose church is fixed when the write is made (#7830).
 *
 * TanStack swaps a pending mutation's options on every render, and a paused one (offline, with
 * networkMode "online") reads `mutationFn` only when it resumes. A `mutationFn` that reads
 * `organizationId` from the render closure therefore writes to whichever church is on screen when
 * the connection returns. Here the church travels in the variables instead: `mutate(vars)` adds
 * `organizationId` from the render the call was made in, and every option callback receives it back.
 */
export function useOrgPinnedMutation<TData, TVars, TContext = unknown>(
  options: UseMutationOptions<TData, Error, OrgPinned<TVars>, TContext>,
): OrgPinnedMutation<TData, TVars, TContext> {
  const { organizationId } = useOrganization();
  const mutation = useMutation(options);
  const { mutate: mutateWrite, mutateAsync: mutateWriteAsync } = mutation;
  const mutate = useCallback(
    (variables: TVars, mutateOptions?: MutateOptions<TData, Error, OrgPinned<TVars>, TContext>) =>
      mutateWrite({ ...variables, organizationId }, mutateOptions),
    [mutateWrite, organizationId],
  );
  const mutateAsync = useCallback(
    (variables: TVars, mutateOptions?: MutateOptions<TData, Error, OrgPinned<TVars>, TContext>) =>
      mutateWriteAsync({ ...variables, organizationId }, mutateOptions),
    [mutateWriteAsync, organizationId],
  );
  return { ...mutation, mutate, mutateAsync };
}
